import {
  Children,
  Fragment,
  isValidElement,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { SurfaceProvider } from "~/lib/surface-context";
import { cn } from "~/lib/utils";

/**
 * Walk children recursively, inlining any fragments.
 *
 * `React.Children.toArray` does not unwrap Fragment elements, so conditional
 * expressions like `cond ? <>...</> : null` appear to PageShell as a single
 * Fragment child. We need to see past the Fragment to find Header / Main /
 * Rail / Hero by component identity.
 */
function flattenChildren(children: ReactNode): ReactElement[] {
  const out: ReactElement[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === Fragment) {
      const fragmentChildren = (child.props as { children?: ReactNode })
        .children;
      out.push(...flattenChildren(fragmentChildren));
    } else {
      out.push(child);
    }
  });
  return out;
}

export type PageShellSize = "compact" | "default" | "wide";
export type PageShellPadY = "default" | "none";
export type PageShellRailWidth = "sm" | "md" | "lg";

interface PageShellProps extends HTMLAttributes<HTMLDivElement> {
  size?: PageShellSize;
  padY?: PageShellPadY;
  contentClassName?: string;
  children: ReactNode;
}

interface PageShellHeroProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

interface PageShellHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

interface PageShellMainProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

interface PageShellRailProps extends HTMLAttributes<HTMLElement> {
  width?: PageShellRailWidth;
  children: ReactNode;
}

const SIZE_CLASSES: Record<PageShellSize, string> = {
  compact: "max-w-4xl",
  default: "max-w-7xl",
  wide: "max-w-8xl",
};

const PAD_Y_CLASSES: Record<PageShellPadY, string> = {
  default: "py-10",
  none: "",
};

const RAIL_WIDTH_CLASSES: Record<PageShellRailWidth, string> = {
  sm: "lg:w-rail-sm",
  md: "lg:w-rail-md",
  lg: "lg:w-rail-lg",
};

function PageShellHero({ children, className, ...rest }: PageShellHeroProps) {
  return (
    <div
      className={cn("w-full", className)}
      data-slot="page-shell-hero"
      {...rest}
    >
      {children}
    </div>
  );
}

function PageShellHeader({
  children,
  className,
  ...rest
}: PageShellHeaderProps) {
  return (
    <div
      className={cn("min-w-0", className)}
      data-slot="page-shell-header"
      {...rest}
    >
      {children}
    </div>
  );
}

function PageShellMain({ children, className, ...rest }: PageShellMainProps) {
  return (
    <div
      className={cn("flex min-w-0 flex-1 flex-col gap-6", className)}
      data-slot="page-shell-main"
      {...rest}
    >
      {children}
    </div>
  );
}

function PageShellRail({
  children,
  className,
  width = "md",
  ...rest
}: PageShellRailProps) {
  return (
    <aside
      className={cn(
        // 64px pinned app header + 16px breathing room.
        "min-w-0 lg:shrink-0 lg:self-start lg:sticky lg:top-20",
        RAIL_WIDTH_CLASSES[width],
        className,
      )}
      data-slot="page-shell-rail"
      {...rest}
    >
      {children}
    </aside>
  );
}

export function PageShell({
  children,
  className,
  contentClassName,
  padY = "default",
  size = "default",
  ...rest
}: PageShellProps) {
  const validChildren = flattenChildren(children);
  const hero = validChildren.find((child) => child.type === PageShellHero);
  const header = validChildren.find((child) => child.type === PageShellHeader);
  const main = validChildren.find((child) => child.type === PageShellMain);
  const rail = validChildren.find((child) => child.type === PageShellRail);

  return (
    <div
      className={cn("relative flex-1", className)}
      data-slot="page-shell"
      {...rest}
    >
      <SurfaceProvider value={1}>
        {hero}
        <div
          className={cn(
            "mx-auto flex w-full flex-col gap-6 px-4",
            SIZE_CLASSES[size],
            PAD_Y_CLASSES[padY],
            contentClassName,
          )}
          data-slot="page-shell-content"
        >
          {header}
          {rail ? (
            <div className="flex flex-col gap-6 lg:flex-row">
              {main}
              {rail}
            </div>
          ) : (
            main
          )}
        </div>
      </SurfaceProvider>
    </div>
  );
}

PageShell.Hero = PageShellHero;
PageShell.Header = PageShellHeader;
PageShell.Main = PageShellMain;
PageShell.Rail = PageShellRail;
